<template>
  <div>
    <label :for="fieldId" class="btn btn--default btn--block ">
      <slot name="label">{{ label }}</slot>
    </label>
    <input :id="fieldId" type="file" :accept="accept" @change="handleFileChange">

    <label class="drop-zone"
           :for="fieldId"
           :class="{'drop-zone--active': dragging,'drop-zone--success': success}"
           @dragover="handleDragOver"
           @dragleave="handleDragLeave"
           @drop="handleDrop">
      <div v-if="notice" :class="noticeType">{{ notice }}</div>
      <div v-else>
        <slot name="dropping">Drop it!</slot>
      </div>
    </label>
  </div>
</template>

<script>
export default {
  name: 'DropZone',
  props: {
    label: { required: false, type: String, default: 'Select a file' },
    allowedMimes: { required: false, type: Array, default: () => [] },
    fieldId: { required: true, type: String },
    accept: { required: false, type: String, default: '' },
  },
  data () {
    return {
      dragging: false,
      success: false,
      notice: '',
      noticeType: '',
    }
  },
  methods: {
    /**
     * Resets the zone
     */
    resetZone () {
      this.setNotice('')
      this.dragging = false
    },
    /**
     * Extract files from dropped content
     */
    extractFiles (dataTransfer) {
      if (dataTransfer.files && dataTransfer.files.length > 0) {
        return dataTransfer.files
      } else {
        this.setNotice('No file selected', 'error')
      }
    },
    /**
     * Handler for changes in input
     */
    handleFileChange () {
      const field = document.getElementById(this.fieldId)
      if (this.checkFiles(field.files)) this.updateFileList()
    },
    /**
     * Handler for drop event on drop zone
     * @param { DragEvent } event - The DOM event
     */
    handleDrop (event) {
      this.preventDefaults(event)

      this.resetZone()

      const files = this.extractFiles(event.dataTransfer)
      if (!this.checkFiles(files)) return

      document.getElementById(this.fieldId).files = files
      this.updateFileList()
    },
    /**
     * Handler when dragging over the drop zone
     * @param { DragEvent } event - The DOM event
     */
    handleDragOver (event) {
      this.preventDefaults(event)
      if (this.dragging) return

      this.dragging = true
      this.setNotice('Drop it like it\'s hot')
    },
    /**
     * Handler when leaving the drop zone
     * @param { DragEvent } event - The DOM event
     */
    handleDragLeave (event) {
      this.preventDefaults(event)
      if (!this.dragging) return

      this.resetZone()
    },

    /**
     * Checks for files
     * @param { Array } items - List of files
     * @return { boolean }
     */
    checkFiles (items) {
      if (!items || items.length === 0) {
        this.setNotice('No file dropped', 'error')
        return false
      }

      if (items.length > 1) {
        this.setNotice('You dropped too much files', 'error')
        return false
      } else {
        for (const item of items) {
          if (!this.checkAllowedMimes(item.type)) {
            if (this.multiple) {
              this.setNotice('Some files are of the wrong type', 'error')
            } else {
              this.setNotice('File type is not supported', 'error')
            }
            return false
          }
        }
      }

      return true
    },

    /**
     * Checks for a file type
     * @param {String} type - The mime type
     * @returns {boolean}
     */
    checkAllowedMimes (type) {
      if (this.allowedMimes.length === 0) return true

      return this.allowedMimes.indexOf(type) > -1
    },
    /**
     * Updates the notice info box
     * @param { String } text - New text
     * @param { String } type - Notice type (check CSS for variants)
     */
    setNotice (text, type = null) {
      this.notice = text
      this.noticeType = type
    },

    /**
     * Updates the file list in input
     * @emits change
     */
    updateFileList () {
      const files = document.getElementById(this.fieldId).files
      let string = ''
      if (files.length > 1) {
        const names = []
        for (const file of files) names.push(file.name)
        string = names.join(', ')
      } else {
        string = `${files[0].name} -`// ${prettyBytes(files[0].size)}`
      }

      this.resetZone()
      this.setNotice(string)
      this.$emit('change', files)
    },

    /**
     * Prevents default event and propagation
     * @param { Event } event
     */
    preventDefaults (event) {
      event.preventDefault()
      event.stopPropagation()
    },
  },
}
</script>
